/**
 * The knowledge base a client's bot answers from.
 *
 * Kinds mirror the "Formulir basis pengetahuan" in Buku Panduan 12.2 so the
 * specialist can file each item from the client's materials exactly where the
 * form put it, and the Console can show what a project is still missing.
 */
export const KNOWLEDGE_KINDS = [
  "profile",
  "offering",
  "operations",
  "faq",
  "legal",
  "policy",
  "document",
] as const;
export type KnowledgeKind = (typeof KNOWLEDGE_KINDS)[number];

export const KNOWLEDGE_KIND_LABELS: Record<KnowledgeKind, string> = {
  profile: "Profil usaha",
  offering: "Layanan, produk & harga",
  operations: "Jam buka, lokasi & kontak",
  faq: "Tanya jawab (FAQ)",
  legal: "Legalitas",
  policy: "Kebijakan (pengiriman, pembayaran, garansi)",
  document: "Dokumen panjang",
};

/** Kinds every published bot should cover; the Console flags gaps against this. */
export const ESSENTIAL_KINDS: readonly KnowledgeKind[] = ["profile", "offering", "operations", "faq"];

export interface KnowledgeEntry {
  id: string;
  kind: KnowledgeKind;
  title: string;
  body: string;
  tags: string[];
  /** Always sent to the model, even when retrieval is narrowing the rest. */
  pinned: boolean;
  /**
   * Key of the Linkage referral that shows where this fact lives on the site
   * (e.g. the founders section of the About page). When an answer leans on
   * this entry, that referral is offered first.
   */
  link?: string | null;
}

export interface KnowledgeChunk {
  id: string;
  entryId: string;
  kind: KnowledgeKind;
  title: string;
  text: string;
  /** The entry's tags — retrieval hints the specialist curates; never shown to the model. */
  tags: string[];
  tokens: number;
  pinned: boolean;
  /** The entry's Linkage referral key, if any. */
  link?: string | null;
}

/** An immutable, published compilation of a project's entries. */
export interface KnowledgeSnapshot {
  version: number;
  entries: KnowledgeEntry[];
  chunks: KnowledgeChunk[];
  tokens: number;
}

/**
 * Up to this size the whole knowledge base rides in the cached system prompt:
 * answers see everything, and with prompt caching the repeat cost is ~10%.
 * Beyond it we retrieve. OpenAI only caches prefixes of 1,024+ tokens, so a
 * very small base pays full input price either way — still cheaper than a miss.
 */
export const FULL_CONTEXT_TOKEN_BUDGET = 9_000;
/** How much retrieved text a single answer may carry when retrieving. */
export const RETRIEVAL_TOKEN_BUDGET = 2_800;

const TARGET_CHUNK_CHARS = 1_400;

/**
 * ~3.5 characters per token on Indonesian prose, on the cautious side: GPT
 * tokenizers usually do a little better. Used for budgeting and Console display only — the
 * ledger always bills the provider's reported usage, never this estimate.
 */
export function estimateTokens(text: string): number {
  return Math.ceil(text.length / 3.5);
}

export function compileSnapshot(version: number, entries: readonly KnowledgeEntry[]): KnowledgeSnapshot {
  const ordered = [...entries].sort(
    (a, b) => KNOWLEDGE_KINDS.indexOf(a.kind) - KNOWLEDGE_KINDS.indexOf(b.kind),
  );
  const chunks = ordered.flatMap(chunkEntry);
  return {
    version,
    entries: ordered,
    chunks,
    tokens: estimateTokens(renderKnowledge(ordered)),
  };
}

/**
 * Short entries stay whole. Long ones split on markdown headings, then on
 * paragraphs, and every piece keeps its entry title so a retrieved fragment
 * still says what it is about.
 */
export function chunkEntry(entry: KnowledgeEntry): KnowledgeChunk[] {
  const make = (text: string, index: number): KnowledgeChunk => ({
    id: `${entry.id}#${index}`,
    entryId: entry.id,
    kind: entry.kind,
    title: entry.title,
    text,
    tags: entry.tags,
    tokens: estimateTokens(`${entry.title}\n${text}`),
    pinned: entry.pinned,
    link: entry.link ?? null,
  });

  const body = entry.body.trim();
  if (body.length <= TARGET_CHUNK_CHARS) return [make(body, 0)];

  const pieces: string[] = [];
  let current = "";
  for (const block of splitBlocks(body)) {
    if (current && current.length + block.length + 2 > TARGET_CHUNK_CHARS) {
      pieces.push(current);
      current = "";
    }
    current = current ? `${current}\n\n${block}` : block;
  }
  if (current) pieces.push(current);
  return pieces.map(make);
}

/** Splits on headings first, then paragraphs; an oversized paragraph splits on sentences. */
function splitBlocks(body: string): string[] {
  return body
    .split(/\n(?=#{1,6}\s)|\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean)
    .flatMap((block) =>
      block.length <= TARGET_CHUNK_CHARS ? [block] : (block.match(/[^.!?]+[.!?]+|\S[^.!?]*$/g) ?? [block]),
    );
}

/** Markdown the model reads: grouped by kind, each entry under its own heading. */
export function renderKnowledge(entries: readonly Pick<KnowledgeEntry, "kind" | "title" | "body">[]): string {
  const sections: string[] = [];
  for (const kind of KNOWLEDGE_KINDS) {
    const ofKind = entries.filter((entry) => entry.kind === kind);
    if (ofKind.length === 0) continue;
    const items = ofKind.map((entry) => `### ${entry.title}\n${entry.body.trim()}`);
    sections.push(`## ${KNOWLEDGE_KIND_LABELS[kind]}\n\n${items.join("\n\n")}`);
  }
  return sections.join("\n\n");
}

export function renderChunks(chunks: readonly KnowledgeChunk[]): string {
  return chunks.map((chunk) => `### ${chunk.title} (${KNOWLEDGE_KIND_LABELS[chunk.kind]})\n${chunk.text}`).join("\n\n");
}

/** Kinds from ESSENTIAL_KINDS that a set of entries does not cover yet. */
export function missingEssentials(entries: readonly Pick<KnowledgeEntry, "kind">[]): KnowledgeKind[] {
  const present = new Set(entries.map((entry) => entry.kind));
  return ESSENTIAL_KINDS.filter((kind) => !present.has(kind));
}
